import { createApi } from '@reduxjs/toolkit/query/react';
import { getHeaders } from 'auth/auth';
import { baseQueryWithReauth } from '../baseQuery';

/**
 * V2 User Management API Service
 * Connects to backend_v2 user management endpoints.
 */
export const userManagementV2API = createApi({
  reducerPath: 'userManagementV2',
  tagTypes: [
    'Users',
    'UserDetails',
    'Invitations',
    'UserRoles',
    'UserBranches',
    'InvitationAnalytics',
    'VerifiedUsers',
  ],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    listUsers: builder.query({
      query: ({ skip = 0, limit = 100, role, branch, is_active, search } = {}) => {
        const params = new URLSearchParams({
          skip: String(skip),
          limit: String(limit),
        });

        if (role) params.append('role', role);
        if (branch) params.append('branch', branch);
        if (is_active !== undefined) params.append('is_active', String(is_active));
        if (search) params.append('search', search);

        return {
          url: `/b2b/users/?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Users'],
    }),

    // List consumer users (and pending invitations) for the organization.
    // Mirrors frontend_v2 behaviour and calls /b2b/users/consumers-list.
    listConsumers: builder.query({
      query: ({ skip = 0, limit = 100, search } = {}) => {
        const params = new URLSearchParams({
          skip: String(skip),
          limit: String(limit),
        });

        if (search) {
          params.append('search', search);
        }

        return {
          url: `/b2b/users/consumers-list?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Users'],
    }),

    listInvitations: builder.query({
      // `search` is applied SERVER-side. Filtering the response client-side
      // only ever saw the first `limit` rows, so anyone past that was
      // unfindable no matter what was typed.
      query: ({ status_filter, exclude_roles, search, skip = 0, limit = 100 } = {}) => {
        const params = new URLSearchParams({
          skip: String(skip),
          limit: String(limit),
        });

        if (status_filter) {
          params.append('status_filter', status_filter);
        }
        if (exclude_roles) {
          params.append('exclude_roles', exclude_roles);
        }
        if (search && search.trim()) {
          params.append('search', search.trim());
        }

        return {
          url: `/b2b/users/invitations?${params.toString()}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['Invitations'],
    }),

    resendInvitation: builder.mutation({
      query: (invitationCode) => ({
        url: `/b2b/users/invitations/${invitationCode}/resend`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: ['Invitations'],
    }),

    sendConsumerInvitation: builder.mutation({
      query: (consumerUserId) => ({
        url: `/b2b/users/consumers/${consumerUserId}/send-invite`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    // Invite a new user (including consumers) to the organization
    inviteUser: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite',
        method: 'POST',
        headers: getHeaders(),
        // Backend expects the payload wrapped in { body: { ... } }
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    inviteConsumerAsOfficer: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite-user-officer',
        method: 'POST',
        headers: getHeaders(),
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    // Invite a new employee to the organization (employee-focused wrapper)
    inviteEmployee: builder.mutation({
      query: (inviteData) => ({
        url: '/b2b/users/invite-employee',
        method: 'POST',
        headers: getHeaders(),
        // Backend expects the payload wrapped in { body: { ... } }
        body: { body: inviteData },
      }),
      invalidatesTags: ['Users', 'Invitations'],
    }),

    // Get employee types (employment types) for dropdowns
    getEmployeeTypes: builder.query({
      query: () => ({
        url: '/lookup/employee-types',
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // Get available roles for the organization
    getAvailableRoles: builder.query({
      query: () => ({
        url: '/b2b/users/roles',
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => {
        const baseRoles = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];

        const hasConsumer = baseRoles.some((role) => role?.value === 'consumer');
        const data = hasConsumer
          ? baseRoles
          : [...baseRoles, { value: 'consumer', label: 'Consumer' }];

        if (response && typeof response === 'object' && !Array.isArray(response)) {
          return { ...response, data };
        }
        return { data };
      },
      providesTags: ['UserRoles'],
    }),

    getUserBranches: builder.query({
      query: (userId) => ({
        url: `/b2b/users/${userId}/branches`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['UserBranches'],
    }),
  }),
});

export const {
  useListUsersQuery,
  useListConsumersQuery,
  useListInvitationsQuery,
  useResendInvitationMutation,
  useSendConsumerInvitationMutation,
  useInviteUserMutation,
  useInviteConsumerAsOfficerMutation,
  useInviteEmployeeMutation,
  useGetEmployeeTypesQuery,
  useGetAvailableRolesQuery,
  useGetUserBranchesQuery,
} = userManagementV2API;
